import React, { useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  useDisclosure,
  Spinner,
  Text
} from '@chakra-ui/react';
import { AddIcon, EditIcon, DeleteIcon } from '@chakra-ui/icons';
import useUserManagement from '../../hooks/admin/useUserManagement';
import { useAuth } from '../../contexts/AuthContext';

const UserManagement = () => {
  const { currentUser: loggedInUser } = useAuth();
  const { users, loading, error, createUser, updateUser, deleteUser } = useUserManagement();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    role: 'user'
  });

  // フォームの入力を処理
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // ユーザー作成または更新をハンドル
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        // passwordが空欄の場合は更新対象から除外
        const updateData = { ...formData };
        if (!updateData.password) delete updateData.password;
        
        // ログを追加して問題を診断
        console.log('更新対象ユーザー:', editingUser);
        console.log('更新データ:', updateData);
        
        // editingUser.idではなく、editingUser.emailを使用
        await updateUser(editingUser.email, updateData);
      } else {
        await createUser(formData);
      }
      onClose();
      // フォームをリセット
      setFormData({ email: '', password: '', role: 'user' });
      setEditingUser(null);
    } catch (err) {
      console.error('エラー:', err);
    }
  };

  // 編集モーダルを開く
  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      password: '', // パスワードは空にする（更新時にパスワードを入力しなければ変更しない）
      role: user.role
    });
    onOpen();
  };

  // 新規作成モーダルを開く
  const handleCreate = () => {
    setEditingUser(null);
    setFormData({ email: '', password: '', role: 'user' });
    onOpen();
  };

  // ユーザー削除を確認して実行
  const handleDelete = async (userId) => {
    if (window.confirm('このユーザーを削除してもよろしいですか？')) {
      try {
        await deleteUser(userId);
      } catch (err) {
        console.error('削除エラー:', err);
      }
    }
  };

  if (loading) return <Spinner />;
  if (error) return <Text color="red.500">エラーが発生しました: {error}</Text>;

  // 管理者は privileged ユーザーを見ることができない
  const filteredUsers = users.filter(user => {
    if (loggedInUser?.role === 'admin' && user.role === 'privileged') {
      return false;
    }
    return true;
  });

  return (
    <Box>
      <Box mb={4} display="flex" justifyContent="space-between" alignItems="center">
        <Text fontSize="xl" fontWeight="bold">ユーザー管理</Text>
        <Button leftIcon={<AddIcon />} colorScheme="blue" onClick={handleCreate}>
          新規ユーザー作成
        </Button>
      </Box>

      <Table variant="simple">
        <Thead>
          <Tr>
            <Th>メールアドレス</Th>
            <Th>権限</Th>
            <Th>アクション</Th>
          </Tr>
        </Thead>
        <Tbody>
          {filteredUsers.map(user => (
            <Tr key={user.id}>
              <Td>{user.email}</Td>
              <Td>{user.role}</Td>
              <Td>
                <IconButton
                  aria-label="Edit"
                  icon={<EditIcon />}
                  size="sm"
                  mr={2}
                  onClick={() => handleEdit(user)}
                />
                <IconButton
                  aria-label="Delete"
                  icon={<DeleteIcon />}
                  size="sm"
                  colorScheme="red"
                  onClick={() => handleDelete(user.id)}
                />
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>

      {/* ユーザー作成/編集モーダル */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editingUser ? 'ユーザー編集' : '新規ユーザー作成'}</ModalHeader>
          <ModalCloseButton />
          <form onSubmit={handleSubmit}>
            <ModalBody>
              <FormControl mb={4} isRequired>
                <FormLabel>メールアドレス</FormLabel>
                <Input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                />
              </FormControl>
              <FormControl mb={4} isRequired={!editingUser}>
                <FormLabel>{editingUser ? 'パスワード（変更する場合のみ）' : 'パスワード'}</FormLabel>
                <Input
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                />
              </FormControl>
              <FormControl mb={4}>
                <FormLabel>権限</FormLabel>
                <Select name="role" value={formData.role} onChange={handleChange}>
                  <option value="user">一般ユーザー</option>
                  <option value="admin">管理者</option>
                  <option value="privileged">特権管理者</option>
                </Select>
              </FormControl>
            </ModalBody>
            <ModalFooter>
              <Button mr={3} onClick={onClose}>キャンセル</Button>
              <Button type="submit" colorScheme="blue">
                {editingUser ? '更新' : '作成'}
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default UserManagement;